import React, { useState } from 'react';
import { requestAllPermissions, AllPermissions } from '../../utils/permissions';
import './welcome-message.scss';

interface WelcomeMessageProps {
  onComplete: (permissions: AllPermissions) => void;
  language?: string;
}

const TEXTS: Record<string, {
  title: string;
  subtitle: string;
  permTitle: string;
  location: string;
  locationDesc: string;
  mic: string;
  micDesc: string;
  camera: string;
  cameraDesc: string;
  privacy: string;
  privacyDetails: string[];
  continue: string;
  requesting: string;
}> = {
  en: {
    title: 'Dubai Police Emergency Assistant',
    subtitle: 'Available 24/7 for your safety and security',
    permTitle: 'To assist you better, we need access to:',
    location: 'Location',
    locationDesc: 'Highly recommended – helps us send help to your exact location',
    mic: 'Microphone',
    micDesc: 'Required for voice communication with the assistant',
    camera: 'Camera',
    cameraDesc: 'For sharing images or video of the situation if needed',
    privacy: 'Privacy Notice',
    privacyDetails: [
      'Location is only used to assist emergency response',
      'Your data is processed securely and in accordance with Dubai Police privacy policy',
      'All communication is encrypted',
    ],
    continue: 'Continue',
    requesting: 'Requesting permissions…',
  },
  ar: {
    title: 'مساعد شرطة دبي للطوارئ',
    subtitle: 'متاح على مدار الساعة لسلامتك وأمانك',
    permTitle: 'لمساعدتك بشكل أفضل، نحتاج إلى:',
    location: 'الموقع',
    locationDesc: 'موصى به بشدة – يساعدنا في إرسال المساعدة إلى موقعك الدقيق',
    mic: 'الميكروفون',
    micDesc: 'مطلوب للتواصل الصوتي مع المساعد',
    camera: 'الكاميرا',
    cameraDesc: 'لمشاركة صور أو فيديو من الموقع عند الحاجة',
    privacy: 'إشعار الخصوصية',
    privacyDetails: [
      'يُستخدم الموقع فقط لمساعدة الاستجابة للطوارئ',
      'تتم معالجة بياناتك بأمان وفقاً لسياسة خصوصية شرطة دبي',
      'جميع الاتصالات مشفرة',
    ],
    continue: 'استمرار',
    requesting: 'جارٍ طلب الأذونات…',
  },
  ur: {
    title: 'دبئی پولیس ایمرجنسی اسسٹنٹ',
    subtitle: 'آپ کی حفاظت کے لیے 24/7 دستیاب',
    permTitle: 'آپ کی بہتر مدد کے لیے، ہمیں ضرورت ہے:',
    location: 'مقام',
    locationDesc: 'انتہائی تجویز کردہ – آپ کے صحیح مقام پر مدد بھیجنے میں مدد کرتا ہے',
    mic: 'مائیکروفون',
    micDesc: 'اسسٹنٹ کے ساتھ آواز سے بات چیت کے لیے ضروری ہے',
    camera: 'کیمرا',
    cameraDesc: 'اگر ضروری ہو تو صورتحال کی تصاویر یا ویڈیو شیئر کرنے کے لیے',
    privacy: 'رازداری کا نوٹس',
    privacyDetails: [
      'مقام صرف ہنگامی ردعمل میں مدد کے لیے استعمال ہوتا ہے',
      'آپ کا ڈیٹا دبئی پولیس کی پرائیویسی پالیسی کے مطابق محفوظ طریقے سے پروسیس کیا جاتا ہے',
      'تمام مواصلات انکرپٹڈ ہیں',
    ],
    continue: 'جاری رکھیں',
    requesting: 'اجازتیں مانگی جا رہی ہیں…',
  },
};

const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ onComplete, language = 'en' }) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const t = TEXTS[language] || TEXTS['en'];

  const handleContinue = async () => {
    setIsRequesting(true);
    setPermissionError(null);
    try {
      const permissions = await requestAllPermissions();
      if (!permissions.microphone.granted) {
        setPermissionError(
          language === 'ar'
            ? 'تم رفض إذن الميكروفون. يمكنك الاستمرار مع وظائف محدودة.'
            : language === 'ur'
            ? 'مائیکروفون کی اجازت مسترد کر دی گئی۔ آپ محدود فعالیت کے ساتھ جاری رکھ سکتے ہیں۔'
            : 'Microphone permission denied. You can continue with limited functionality.'
        );
      }
      setTimeout(() => {
        onComplete(permissions);
      }, permissionError ? 2000 : 400);
    } catch {
      setTimeout(() => {
        onComplete({
          location: { granted: false },
          microphone: { granted: false },
          camera: { granted: false },
        });
      }, 1000);
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <div className="welcome-message">
      <div className="welcome-content">
        {/* Dubai Police Logo / Header */}
        <div className="welcome-header">
          <div className="dp-logo-badge">🚔</div>
          <h1>{t.title}</h1>
          <p className="welcome-subtitle">{t.subtitle}</p>
        </div>

        {/* Permissions */}
        <div className="permissions-section">
          <h3>{t.permTitle}</h3>
          <div className="permissions-list">
            <div className="permission-item location-highlight">
              <span className="perm-icon">📍</span>
              <div className="permission-details">
                <strong>{t.location} ⭐</strong>
                <span>{t.locationDesc}</span>
              </div>
            </div>
            <div className="permission-item">
              <span className="perm-icon">🎙️</span>
              <div className="permission-details">
                <strong>{t.mic}</strong>
                <span>{t.micDesc}</span>
              </div>
            </div>
            <div className="permission-item">
              <span className="perm-icon">📷</span>
              <div className="permission-details">
                <strong>{t.camera}</strong>
                <span>{t.cameraDesc}</span>
              </div>
            </div>
          </div>
        </div>

        {showPrivacy && (
          <div className="privacy-details">
            <h4>{t.privacy}</h4>
            <ul>
              {t.privacyDetails.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {permissionError && (
          <div className="permission-error">
            <p>{permissionError}</p>
          </div>
        )}

        <div className="welcome-actions">
          <button
            className="btn-primary"
            onClick={handleContinue}
            disabled={isRequesting}
          >
            {isRequesting ? t.requesting : t.continue}
          </button>
          <button
            className="btn-secondary"
            onClick={() => setShowPrivacy(!showPrivacy)}
          >
            {t.privacy}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeMessage;
